import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, ChevronDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux'; 
import { toast } from 'sonner';

// ✅ Config Imports
import { IS_DEV, TEST_REAL_API } from "@/config/env";

import { 
  lawyerVerificationSchema, 
  type LawyerVerificationFormData,
  INDIAN_STATES 
} from './lawyerVerificationSchema';

// 🔥 UPDATE: Import from your new specific API file
import { saveLawyerVerification } from '../../../../../api/onboardingApis/lawyerOnboardingApi/lawyerVerification.api'; 

import type { RootState } from '@/store/store'; 

interface LawyerStep2Props {
  onNext?: (data: LawyerVerificationFormData) => void;
}

const COURTS = ["District Court", "High Court", "Supreme Court"];

const LawyerStep2 = ({ onNext }: LawyerStep2Props) => {
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCourtDropdown, setShowCourtDropdown] = useState(false);
  const courtDropdownRef = useRef<HTMLDivElement>(null);

  // 1. Get Personal Data from Redux
  const registrationData = useSelector((state: RootState) => state.expert.registrationData);

  // 🟢 FIXED: Added mode: "onChange" and destructured isValid to trace validation locks in real-time
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<LawyerVerificationFormData>({
    resolver: zodResolver(lawyerVerificationSchema),
    mode: "onChange",
    defaultValues: {
      barCouncil: '',
      registrationNumber: '',
      year: '',
      courtPractice: [],
      certificate: undefined
    }
  });

  const selectedCourts = watch('courtPractice') || [];

  // Dropdown Logic
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (courtDropdownRef.current && !courtDropdownRef.current.contains(event.target as Node)) {
        setShowCourtDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCourtToggle = (court: string) => {
    let updatedCourts: string[];
    if (selectedCourts.includes(court)) {
      updatedCourts = selectedCourts.filter((c) => c !== court);
    } else {
      updatedCourts = [...selectedCourts, court];
    }
    setValue("courtPractice", updatedCourts, { shouldValidate: true });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setValue('certificate', files, { shouldValidate: true });
      setFileName(files[0].name);
    }
  };

  // ⚡ DEV SKIP HANDLER
  const handleDevSkip = async () => {
    await new Promise(resolve => setTimeout(resolve, 800));

    const mockData: LawyerVerificationFormData = {
        barCouncil: 'bar-council-delhi',
        registrationNumber: 'DEL/123/2023',
        year: '2023',
        courtPractice: ['High Court'],
        certificate: undefined 
    };

    toast.success("⚡ Mock Verification Successful (Dev Mode)");
    
    if (onNext) {
        onNext(mockData);
    }
  };

  // 🟢 SUBMIT HANDLER
  const onSubmit = async (data: LawyerVerificationFormData) => {
    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting verification details...');
    
    try {
      if (IS_DEV && !TEST_REAL_API) {
         await handleDevSkip();
         toast.dismiss(loadingToast);
         return; 
      }

      const expertCode = localStorage.getItem('expertCode');
      
      if (!expertCode) {
        toast.error("Session missing. Please login again.", { id: loadingToast });
        return;
      }

      const formData = new FormData();
      formData.append('expertCode', expertCode);
      formData.append('name', registrationData?.fullName || '');
      formData.append('email', registrationData?.email || '');
      formData.append('phoneNumber', registrationData?.phone || '');
      formData.append('barCouncil', data.barCouncil || '');
      formData.append('registrationNumber', data.registrationNumber || '');
      formData.append('year', data.year || '');
      formData.append('courtPractice', (data.courtPractice || []).join(', '));
      
      if (data.certificate && data.certificate.length > 0) {
        formData.append('certificate', data.certificate[0]);
      }

      await saveLawyerVerification(formData);

      toast.success("Verification details saved! Moving to next step.", { id: loadingToast });

      if (onNext) {
        onNext(data); 
      }

    } catch (error: any) {
      console.error("Submission Error:", error);
      
      // 🟢 FIXED: Injected standard clear error parsing for arrays or nested server data strings
      const serverMessage = error.response?.data?.message;
      const formattedError = Array.isArray(serverMessage) ? serverMessage[0] : serverMessage;
      
      toast.error(formattedError || "Failed to save verification data. Please try again.", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🟢 FIXED: Locks submission path if inputs are missing or background requests are processing
  const isButtonDisabled = isSubmitting || !isValid;

  return (
    <div className="min-h-screen bg-brand-blue-900 py-8 px-4 font-['Poppins']">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Lawyer Verification
          </h1>
          <p className="text-teal-100 text-sm md:text-base">
            Please provide your Bar Council registration details
          </p>

          {TEST_REAL_API && IS_DEV && (
            <div className="mt-4 bg-yellow-500/20 text-yellow-200 py-1 px-3 rounded-full inline-block text-xs border border-yellow-500/50">
              ⚠️ Real API Testing Enabled
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Bar Council Information
          </h2>

          {/* 🟢 FIXED: Wrapped elements in a semantic form handler tag */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Bar Council */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bar Council <span className="text-red-500">*</span>
              </label>
              <select
                {...register('barCouncil')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-gray-700"
              >
                <option value="">Select Bar Council</option>
                <option value="bar-council-india">Bar Council of India</option>
                {INDIAN_STATES.map(state => (
                  <option key={state} value={state.toLowerCase().replace(/\s+/g, '-')}>
                    Bar Council of {state}
                  </option>
                ))}
              </select>
              {errors.barCouncil && (
                <p className="text-red-500 text-sm mt-1">{errors.barCouncil.message}</p>
              )}
            </div>

            {/* Registration Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enrollment Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('registrationNumber')}
                placeholder="Enter your Registration Number"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
              />
              {errors.registrationNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.registrationNumber.message}</p>
              )}
            </div>

            {/* Year and Court of Practice */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year of Enrollment <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  {...register('year')}
                  placeholder="YYYY"
                  min="1950"
                  max={new Date().getFullYear()}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                />
                {errors.year && (
                  <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>
                )}
              </div>

              {/* Court of Practice Dropdown */}
              <div ref={courtDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Court of Practice <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div
                    onClick={() => setShowCourtDropdown(!showCourtDropdown)}
                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-gray-900 cursor-pointer flex items-center justify-between"
                  >
                    <span className={selectedCourts.length > 0 ? "text-gray-900" : "text-gray-400"}>
                      {selectedCourts.length > 0 ? selectedCourts.join(", ") : "Select courts"}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCourtDropdown ? "rotate-180" : ""}`} />
                  </div>

                  {showCourtDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {COURTS.map((court) => (
                        <label
                          key={court}
                          className="flex items-center px-4 py-2.5 hover:bg-gray-5 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCourts.includes(court)}
                            onChange={() => handleCourtToggle(court)}
                            className="w-4 h-4 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 mr-3"
                          />
                          <span className="text-gray-900">{court}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.courtPractice && (
                  <p className="text-red-500 text-sm mt-1">{errors.courtPractice.message}</p>
                )}
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bar Council Certificate or ID Card <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-500 transition">
                <input
                  type="file"
                  id="certificate"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="certificate" className="cursor-pointer flex flex-col items-center">
                  {fileName ? (
                    <>
                      <FileText className="w-12 h-12 text-cyan-500 mb-2" />
                      <p className="text-sm text-gray-700 font-medium">{fileName}</p>
                      <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-500">PDF, JPG, PNG (Max 5MB)</p>
                    </>
                  )}
                </label>
              </div>
              {errors.certificate?.message && (
                <p className="text-red-500 text-sm mt-1">{String(errors.certificate.message)}</p>
              )}
            </div>

            {/* ⚡ DEV BUTTON: Only visible in DEV when NOT testing real API */}
            {IS_DEV && !TEST_REAL_API && (
              <button
                type="button"
                onClick={handleDevSkip}
                className="w-full mb-2 bg-yellow-500 hover:bg-yellow-600 text-black py-2 rounded-lg font-bold border-2 border-yellow-700 border-dashed transition-all"
              >
                ⚡ DEV MODE: Skip & Mock Verify
              </button>
            )}

            {/* Submit Button */} 
            <button
              type="submit"
              disabled={isButtonDisabled}
              className={`w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg mt-6 flex justify-center items-center ${
                isButtonDisabled ? 'opacity-50 cursor-not-allowed grayscale' : ''
              }`}
            >
              {isSubmitting ? 'Processing Application...' : 'Proceed to Final Step'}
            </button>

            {!isValid && !isSubmitting && (
              <p className="text-center text-xs text-gray-400 mt-2">
                Please complete all the fields above and upload your certificate to enable submission.
              </p>
            )}

            <p className="text-center text-sm text-gray-500 mt-4">
              Your application will be reviewed within 2-3 business days
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LawyerStep2;